import { Badge, Box, Icon, Stack, Text, Tooltip, useColorModeValue } from '@ecoinvent/ui';
import { FiChevronRight } from 'react-icons/fi';
import { GrCubes } from 'react-icons/gr';

import TooltipBody from 'components/UI/TooltipBody';
import TooltipHeader from 'components/UI/TooltipHeader';
import { FlowsGroup, FlowsGroupNoWaste, FlowsInput, FlowsOutput } from 'hooks/data/datasets/V4/flows/types';

import { groupToIcon, groupToIconBackground, groupToTooltipLabel, groupToTooltipTitle } from '../utils/constants';

const getFlowTooltipBody = (group: FlowsGroup, direction: 'input' | 'output'): string => {
  if (group === 'waste') {
    return direction === 'input'
      ? 'This flow represents waste materials received by the process from other systems for treatment, recycling, reuse or disposal.'
      : 'This flow represents a waste material generated by the process that requires disposal, treatment, or management.';
  }
  return groupToTooltipLabel[group as FlowsGroupNoWaste];
};

type FlowCardProps = {
  variant: 'flow';
  flow: FlowsInput | FlowsOutput;
  direction: 'input' | 'output';
  onViewDataset?: () => void;
};

type DatasetCardProps = {
  variant: 'dataset';
  name: string;
  inputCount: number;
  outputCount: number;
  geography?: string;
  timePeriod?: string;
  unit?: string;
  activityType?: string;
  onViewDataset?: () => void;
};

export type FlowGraphCardProps = FlowCardProps | DatasetCardProps;

const ViewDatasetButton = ({ onClick }: { onClick: () => void }) => {
  const color = useColorModeValue('#38A169', '#68D391');

  return (
    <button
      type="button"
      className="nopan nodrag"
      style={{
        color,
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'none',
        border: 'none',
        fontFamily: 'inherit',
        padding: '0 0 0 28px',
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
        onClick();
      }}
    >
      View detail <FiChevronRight size={13} />
    </button>
  );
};

const FlowGraphCard = (props: FlowGraphCardProps) => {
  const bg = useColorModeValue('white', 'rgba(255,255,255,0.04)');
  const subtextColor = useColorModeValue('#575454', '#AEACAC');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const datasetOutlineColor = useColorModeValue('#3F59DC', '#C3CBF4');
  const datasetBadgeBg = useColorModeValue('#C3CBF4', 'rgba(156,169,237,0.16)');
  const datasetIconColor = useColorModeValue('#101B56', '#9CA9ED');
  const wasteIconColor = '#231F1F';

  if (props.variant === 'dataset') {
    const { name, inputCount, outputCount, geography, timePeriod, unit, activityType, onViewDataset } = props;
    const metaParts = [geography, timePeriod, unit, activityType].filter(Boolean);

    return (
      <Box
        bg={bg}
        borderWidth="1px"
        borderStyle="solid"
        borderColor={borderColor}
        borderRadius="md"
        p={4}
        w="330px"
        boxShadow="sm"
        sx={{ outline: `3px solid ${datasetOutlineColor}`, outlineOffset: '2px' }}
      >
        <Stack spacing={2}>
          <Stack direction="row" align="center" spacing={2}>
            <Tooltip label="Dataset" hasArrow>
              <Badge bg={datasetBadgeBg} p={1} display="flex" alignItems="center" justifyContent="center">
                <Icon as={GrCubes} boxSize="14px" color={datasetIconColor} />
              </Badge>
            </Tooltip>
            <Text fontWeight="semibold" fontSize="md" noOfLines={1}>
              {name}
            </Text>
          </Stack>
          <Text fontSize="sm" color={subtextColor} pl={7}>
            {inputCount} input{inputCount !== 1 ? 's' : ''}, {outputCount} output{outputCount !== 1 ? 's' : ''}
          </Text>
          {metaParts.length > 0 && (
            <Text fontSize="xs" color={subtextColor} pl={7} noOfLines={2}>
              {metaParts.join(' • ')}
            </Text>
          )}
          {onViewDataset && <ViewDatasetButton onClick={onViewDataset} />}
        </Stack>
      </Box>
    );
  }

  const { flow, direction, onViewDataset } = props;
  const leftBorderColor = direction === 'input' ? '#5CD496' : '#9F7AEA';

  return (
    <Box
      bg={bg}
      border="1px"
      borderColor={borderColor}
      borderLeft="4px solid"
      borderLeftColor={leftBorderColor}
      borderRadius="md"
      p={3}
      w="330px"
      boxShadow="sm"
    >
      <Stack spacing={0}>
        <Stack direction="row" align="center" spacing={2}>
          <Tooltip
            hasArrow
            placement="top-start"
            p={2}
            label={
              <Stack spacing={0}>
                <TooltipHeader>{groupToTooltipTitle[flow.group]}</TooltipHeader>
                <TooltipBody>{getFlowTooltipBody(flow.group, direction)}</TooltipBody>
              </Stack>
            }
          >
            <Badge
              colorScheme={groupToIconBackground[flow.group]}
              variant="solid"
              w="24px"
              h="24px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon
                as={groupToIcon[flow.group]}
                boxSize="14px"
                sx={{
                  '& *': { fill: flow.group === 'valuable' ? 'white' : flow.group === 'waste' ? wasteIconColor : 'auto' },
                }}
              />
            </Badge>
          </Tooltip>
          <Stack spacing={1} flex={1} minW={0}>
            <Text fontWeight="semibold" fontSize="md" noOfLines={1}>
              {flow.name}
            </Text>
            {'supplier_count' in flow && flow.supplier_count !== undefined && (
              <Text fontSize="sm" fontWeight="medium" color={subtextColor}>
                {flow.supplier_count} supplier{flow.supplier_count !== 1 ? 's' : ''}
              </Text>
            )}
          </Stack>
        </Stack>
        {onViewDataset && (
          <Box pt={4}>
            <ViewDatasetButton onClick={onViewDataset} />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default FlowGraphCard;
